'use client';

import { TextField, IconButton, Button, styled } from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { useRef, useEffect, useState } from 'react';
import useSearchCount from '@/hooks/studbook/useSearchCount';
import { useDatadogTracker } from '@/hooks/studbook/datadog/useDatadogTracker';
import { EventType } from '@/lib/types';
import useSearch from '@/hooks/studbook/useSearch';
import useSearchPrecheck from '@/hooks/studbook/useSearchPrecheck';
import useStudbookContext from '@/studbook/store/useStudbookContext';
import useSearchStore from '@/studbook/store/useSearchStore';
import useGridStore from '@/studbook/store/useGridStore';
import useCommonFilter from '@/hooks/studbook/useCommonFilter';
import AdvancedFilterDialog from '@/components/publicSearch/Search/AdvancedFilterDialog';
import { CircularProgress } from '@/primitives';

export const SearchTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: 'white',
  textTransform: 'none',
  ['& fieldset']: {
    borderRadius: '8px',
  },
  ['& input']: {
    padding: '12px 16px 12px 0px',
  },
}));

export const SearchCountPillButton = styled(Button)(({ theme }) => ({
  paddingBlock: '6px',
  marginInlineStart: 1,
  marginInlineEnd: 5,
  borderRadius: '4px',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  minWidth: 'max-content',
}));

export default function PublicSearchInput() {
  const { filters } = useCommonFilter();
  const { orgId } = useStudbookContext();
  const {
    searchType,
    basicSearchInput,
    enableSearch,
    openSearchResult,
    setBasicSearch,
  } = useSearchStore();
  const { paginationModel } = useGridStore();
  const inputRef = useRef<HTMLInputElement | undefined>();
  const searchStartTimeRef = useRef<number | null>(null);
  const { startSearch } = useSearch(orgId, paginationModel);
  const { count, loading } = useSearchCount(orgId);
  const { errorShown, showError, hideError } = useSearchPrecheck(
    typeof count === 'undefined',
  );
  const initialValue = useRef<string | null>(basicSearchInput);
  const [value, setValue] = useState(basicSearchInput);

  const { trackEvent } = useDatadogTracker();

  const refocusInputWhenNoResult = () => {
    if (count !== 0 || loading) return;
    if (!inputRef.current) return;

    inputRef.current.focus();
  };

  const errorCheck = () => {
    if (typeof count === 'undefined' && !errorShown) {
      return showError();
    }

    if (typeof count !== 'undefined' && errorShown) {
      hideError();
    }
  };

  const handleSearch = () => {
    let error = errorCheck();
    if (!!error) return;

    hideError();
    searchStartTimeRef.current = performance.now();
    enableSearch(true);
    startSearch();
    openSearchResult();
    setTimeout(refocusInputWhenNoResult, 1200);
  };

  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    if (
      searchStartTimeRef.current !== null &&
      !loading &&
      typeof count === 'number' &&
      orgId
    ) {
      const durationMs = performance.now() - searchStartTimeRef.current;

      trackEvent?.(
        'SearchExecuted',
        {
          searchType,
          searchTerm: basicSearchInput,
          filterCount: filters.length,
          resultCount: count,
          triggeredFrom: 'basic',
          orgId,
          durationMs: Math.round(durationMs),
        },
        EventType.FORM_SUBMIT,
      );
      searchStartTimeRef.current = null;
    }
  }, [
    loading,
    count,
    basicSearchInput,
    filters.length,
    orgId,
    searchType,
    trackEvent,
  ]);

  useEffect(() => {
    // prevent triggering on initial render
    if (value === initialValue.current) return;
    initialValue.current = null;
    enableSearch(false);

    const timeoutId = setTimeout(() => setBasicSearch(value), 500);

    return () => clearTimeout(timeoutId);
  }, [value, enableSearch, setBasicSearch]);

  // clear input when clear filters was pressed or switching between tabs
  useEffect(() => {
    if (filters.length) return;
    initialValue.current = null;
    setValue('');
  }, [filters]);

  const renderButtonLabel = () => {
    if (basicSearchInput && loading) {
      return <CircularProgress size={20} sx={{ color: 'white' }} />;
    }

    if (!basicSearchInput && filters.length === 0) {
      return 'Show all results';
    }

    if (count === 0) {
      return '0 results found';
    }

    return `Show ${count} results`;
  };

  return (
    <>
      <SearchTextField
        fullWidth
        inputRef={inputRef}
        autoFocus
        placeholder={
          searchType === 'animal'
            ? 'Search by animal name or identifier'
            : 'Search by member name or identifier'
        }
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleInputKeyPress}
        InputProps={{
          sx: {
            px: 0.5,
          },
          startAdornment: (
            <IconButton>
              <TravelExploreIcon />
            </IconButton>
          ),
          endAdornment:
            (basicSearchInput && loading) || typeof count === 'number' ? (
              <SearchCountPillButton
                color="info"
                disabled={loading}
                variant="contained"
                onClick={handleSearch}
              >
                {renderButtonLabel()}
              </SearchCountPillButton>
            ) : null,
        }}
      />
      <AdvancedFilterDialog />
    </>
  );
}
